import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Select from "@amzn/awsui-components-react/polaris/select";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import { PRESCRIBERS } from "../../../../../prescription_ingestion/config/PrescriptionDefaults";
import { NewRxPrescriberFormState } from "./NewRxItemTypes";

interface Props {
  prescriber: NewRxPrescriberFormState;
  onChange: (updates: Partial<NewRxPrescriberFormState>) => void;
}

const PRESET_OPTIONS = Object.keys(PRESCRIBERS).map((key) => {
  const p = PRESCRIBERS[key];
  return { value: key, label: `${p.firstName} ${p.lastName} (${p.clinicName})` };
});

/**
 * Prescriber input for one NewRxBatchItem. Captures either a prescriberSpi (lookup) or a full
 * inline NewRxPrescriber. A preset Select (seeded from PrescriptionDefaults.PRESCRIBERS) prefills
 * both the SPI and the inline fields so switching modes keeps the selection. Controlled via a
 * single onChange(updates) callback (parent owns state).
 */
class NewRxPrescriberBlock extends React.Component<Props> {
  private applyPreset = (key: string) => {
    const p = PRESCRIBERS[key];
    if (!p) return;
    this.props.onChange({
      prescriberSpi: p.spi,
      npi: p.npi,
      deaNumber: p.deaNumber,
      firstName: p.firstName,
      lastName: p.lastName,
      addressLine1: p.addressLine1,
      city: p.city,
      state: p.state,
      postalCode: p.zipCode,
      practiceName: p.clinicName,
      phone: p.phoneNumber,
      fax: p.fax && p.fax.length > 0 ? p.fax[0].Number : "",
      // Reset optional fields the preset does not provide, so a prior preset / manual entry
      // does not leave stale values mixed with the newly selected prescriber.
      stateLicenseNumber: "",
      suffix: "",
      specialty: "",
      countryCode: "",
      email: "",
    });
  };

  render() {
    const { prescriber, onChange } = this.props;

    // Reflect the current selection by matching the prescriber's SPI to a preset (SPIs are unique).
    // Clears if the SPI is edited to a non-preset value.
    const selectedPreset =
      PRESET_OPTIONS.find((o) => PRESCRIBERS[o.value].spi === prescriber.prescriberSpi) || null;

    return (
      <Container header={<Header variant="h3">Prescriber</Header>}>
        <SpaceBetween size="m">
          <FormField
            label="Prescriber Preset"
            description="Optional. Prefills SPI and inline details from a known prescriber."
          >
            <Select
              selectedOption={selectedPreset}
              onChange={({ detail }) => this.applyPreset(detail.selectedOption.value as string)}
              options={PRESET_OPTIONS}
              placeholder="Select a preset prescriber (optional)"
              filteringType="auto"
            />
          </FormField>

          <Toggle
            checked={prescriber.useSpi}
            onChange={({ detail }) => onChange({ useSpi: detail.checked })}
          >
            Use Prescriber SPI (lookup)
          </Toggle>

          {prescriber.useSpi ? (
            <FormField label="Prescriber SPI" description="Required">
              <Input
                value={prescriber.prescriberSpi}
                onChange={({ detail }) => onChange({ prescriberSpi: detail.value })}
                placeholder="6633950178005"
              />
            </FormField>
          ) : (
            <SpaceBetween size="m">
              <ColumnLayout columns={2}>
                <FormField label="NPI" description="Required">
                  <Input value={prescriber.npi} onChange={({ detail }) => onChange({ npi: detail.value })} />
                </FormField>
                <FormField label="DEA Number" description="Required">
                  <Input value={prescriber.deaNumber} onChange={({ detail }) => onChange({ deaNumber: detail.value })} />
                </FormField>
                <FormField label="First Name" description="Required">
                  <Input value={prescriber.firstName} onChange={({ detail }) => onChange({ firstName: detail.value })} />
                </FormField>
                <FormField label="Last Name" description="Required">
                  <Input value={prescriber.lastName} onChange={({ detail }) => onChange({ lastName: detail.value })} />
                </FormField>
                <FormField label="Address Line 1" description="Required">
                  <Input value={prescriber.addressLine1} onChange={({ detail }) => onChange({ addressLine1: detail.value })} />
                </FormField>
                <FormField label="City" description="Required">
                  <Input value={prescriber.city} onChange={({ detail }) => onChange({ city: detail.value })} />
                </FormField>
                <FormField label="State" description="Required">
                  <Input value={prescriber.state} onChange={({ detail }) => onChange({ state: detail.value })} />
                </FormField>
                <FormField label="Postal Code" description="Required">
                  <Input value={prescriber.postalCode} onChange={({ detail }) => onChange({ postalCode: detail.value })} />
                </FormField>
              </ColumnLayout>

              <ExpandableSection headerText="Optional prescriber fields">
                <ColumnLayout columns={2}>
                  <FormField label="State License Number">
                    <Input value={prescriber.stateLicenseNumber} onChange={({ detail }) => onChange({ stateLicenseNumber: detail.value })} />
                  </FormField>
                  <FormField label="Suffix">
                    <Input value={prescriber.suffix} onChange={({ detail }) => onChange({ suffix: detail.value })} />
                  </FormField>
                  <FormField label="Specialty">
                    <Input value={prescriber.specialty} onChange={({ detail }) => onChange({ specialty: detail.value })} />
                  </FormField>
                  <FormField label="Practice Name">
                    <Input value={prescriber.practiceName} onChange={({ detail }) => onChange({ practiceName: detail.value })} />
                  </FormField>
                  <FormField label="Country Code">
                    <Input value={prescriber.countryCode} onChange={({ detail }) => onChange({ countryCode: detail.value })} />
                  </FormField>
                  <FormField label="Phone">
                    <Input value={prescriber.phone} onChange={({ detail }) => onChange({ phone: detail.value })} />
                  </FormField>
                  <FormField label="Fax">
                    <Input value={prescriber.fax} onChange={({ detail }) => onChange({ fax: detail.value })} />
                  </FormField>
                  <FormField label="Email">
                    <Input value={prescriber.email} onChange={({ detail }) => onChange({ email: detail.value })} />
                  </FormField>
                </ColumnLayout>
              </ExpandableSection>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
    );
  }
}

export default NewRxPrescriberBlock;
